import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import '../styles/Upload.css';

const genresList = ['Rock', 'Pop', 'Hip-Hop', 'Electronic', 'Jazz', 'Classical', 'Country', 'Blues'];

const Upload = () => {
  const [file, setFile] = useState(null);
  const [coverPhoto, setCoverPhoto] = useState(null);
  const [title, setTitle] = useState('');
  const [genre, setGenre] = useState('');
  const [trackType, setTrackType] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('track');
  const [albumTitle, setAlbumTitle] = useState('');
  const [files, setFiles] = useState([]);
  const [plan, setPlan] = useState(null);
  const auth = getAuth();

  useEffect(() => {
    const fetchUserPlan = async () => {
      try {
        const user = auth.currentUser;
        if (user) {
          const token = await user.getIdToken();
          const response = await axios.get(
            `https://sync-api-troz.onrender.com/api/users/firebase/${user.uid}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setPlan(response.data.plan);
        }
      } catch (error) {
        console.error('Error fetching user plan:', error);
      }
    };

    fetchUserPlan();
  }, [auth.currentUser]);

  const handleSelectSingleFile = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.name.match(/\.(mp3|wav|flac|aac|ogg|wma|m4a|amr|opus|aiff|mid|midi)$/)) {
      setFile(selectedFile);
    } else {
      alert('Invalid audio file format. Please upload a supported file.');
    }
  };

  const handleSelectAlbumFiles = (e) => {
    const selectedFiles = Array.from(e.target.files).filter((f) =>
      f.name.match(/\.(mp3|wav|flac|aac|ogg|wma|m4a|amr|opus|aiff|mid|midi)$/)
    );
    if (selectedFiles.length) {
      const mappedFiles = selectedFiles.map((f) => ({
        file: f,
        title: '',
      }));
      setFiles(mappedFiles);
    } else {
      alert('No valid audio files selected.');
    }
  };

  const handleAddMoreAlbumFiles = (e) => {
    const selectedFiles = Array.from(e.target.files).filter((f) =>
      f.name.match(/\.(mp3|wav|flac|aac|ogg|wma|m4a|amr|opus|aiff|mid|midi)$/)
    );
    if (selectedFiles.length) {
      const newFiles = selectedFiles.map((f) => ({
        file: f,
        title: '',
      }));
      setFiles((prev) => [...prev, ...newFiles]);
    } else {
      alert('No valid audio files selected.');
    }
  };

  const handleRemoveFile = (indexToRemove) => {
    setFiles((prev) => prev.filter((_, idx) => idx !== indexToRemove));
  };

  const handleCoverPhotoChange = (e) => {
    const selectedPhoto = e.target.files[0];
    if (selectedPhoto && selectedPhoto.name.match(/\.(jpg|jpeg|png|gif|webp|bmp|tiff|heic|ico|raw|svg)$/)) {
      setCoverPhoto(selectedPhoto);
    } else {
      alert('Invalid cover photo format. Please upload a supported image file.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const user = auth.currentUser;
    if (!user) {
      alert('User is not authenticated. Please log in and try again.');
      return;
    }

    if (activeTab === 'album') {
      if (!files.length || !albumTitle || !genre) {
        alert('Please fill in all fields and select tracks.');
        return;
      }
    } else {
      if (!file || !title || !genre || !trackType) {
        alert('Please fill in all fields for single track.');
        return;
      }
    }

    const formData = new FormData();
    const firebaseUid = user.uid;

    if (activeTab === 'album') {
      files.forEach((item, index) => {
        formData.append('files', item.file);
        formData.append('titles', item.title || `Track ${index + 1}`);
      });
      formData.append('albumTitle', albumTitle);
    } else {
      formData.append('file', file);
      formData.append('title', title);
      formData.append('trackType', trackType);
    }

    formData.append('genre', genre);
    formData.append('firebaseUid', firebaseUid);

    if (coverPhoto) {
      formData.append('coverPhoto', coverPhoto);
    }

    setLoading(true);

    try {
      const url =
        activeTab === 'album'
          ? 'https://sync-api-troz.onrender.com/api/uploads/album'
          : 'https://sync-api-troz.onrender.com/api/uploads';

      const response = await axios.post(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      alert(activeTab === 'album' ? 'Album uploaded successfully!' : 'Track uploaded successfully!');
      console.log(response.data);
      resetForm();
    } catch (error) {
      console.error('Error uploading:', error);
      if (error.response) {
        switch (error.response.status) {
          case 400:
            alert(error.response.data.message || 'Bad Request. Please check your input and try again.');
            break;
          case 401:
            alert('Unauthorized. Please log in again.');
            break;
          case 403:
            alert(error.response.data.message || 'You have reached your upload limit. Upgrade to premium to upload more tracks.');
            break;
          case 413:
            alert('File size too large. Please upload a smaller file.');
            break;
          case 500:
            alert('Server error. Please try again later.');
            break;
          default:
            alert('Unexpected error occurred. Please try again.');
        }
      } else if (error.request) {
        alert('No response from the server. Please check your internet connection.');
      } else {
        alert('An error occurred while uploading. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFile(null);
    setFiles([]);
    setCoverPhoto(null);
    setTitle('');
    setAlbumTitle('');
    setGenre('');
    setTrackType('');
    setActiveTab('track');
  };

  return (
    <div className="upload-page">
      <header className="tab-container-upload">
        <button
          className={`tab-button ${activeTab === 'track' ? 'active-tab' : ''}`}
          onClick={() => setActiveTab('track')}
        >
          Single Track
        </button>
        <button
          className={`tab-button ${activeTab === 'album' ? 'active-tab' : ''}`}
          onClick={() => setActiveTab('album')}
        >
          Album
        </button>
      </header>

      <div className="main-content-upload">
        <div className="upload-container">
          <h2 className="upload-title">Upload</h2>
          <form onSubmit={handleSubmit} className="upload-form">
            <div className="form-group">
              <label className="form-label">
                {activeTab === 'album' ? 'Album Title' : 'Track Title'}
              </label>
              <input
                type="text"
                value={activeTab === 'album' ? albumTitle : title}
                onChange={(e) => (activeTab === 'album' ? setAlbumTitle(e.target.value) : setTitle(e.target.value))}
                className="form-input"
                placeholder={`Enter ${activeTab === 'album' ? 'album' : 'track'} title`}
                required
              />
            </div>

            {activeTab === 'track' && (
              <div className="form-group">
                <label className="form-label">Track Type</label>
                <div className="picker-container">
                  {['vocals', 'beat', 'both'].map((type) => (
                    <button
                      key={type}
                      type="button"
                      className={`picker-option ${trackType === type ? 'selected' : ''}`}
                      onClick={() => setTrackType(type)}
                    >
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Genre</label>
              <div className="picker-container">
                {genresList.map((g) => (
                  <button
                    key={g}
                    type="button"
                    className={`picker-option ${genre === g ? 'selected' : ''}`}
                    onClick={() => setGenre(g)}
                  >
                    {g}
                  </button>
                ))}
              </div>
            </div>

            {activeTab === 'track' ? (
              <div className="form-group">
                <label className="form-label">Audio File</label>
                <input
                  type="file"
                  accept=".mp3,.wav,.flac,.aac,.ogg,.wma,.m4a,.amr,.opus,.aiff,.mid,.midi"
                  onChange={handleSelectSingleFile}
                  className="file-input"
                />
                {file && <span className="file-name">Selected: {file.name}</span>}
              </div>
            ) : (
              <div className="form-group">
                <label className="form-label">Audio Files</label>
                <input
                  type="file"
                  accept=".mp3,.wav,.flac,.aac,.ogg,.wma,.m4a,.amr,.opus,.aiff,.mid,.midi"
                  multiple
                  onChange={files.length ? handleAddMoreAlbumFiles : handleSelectAlbumFiles}
                  className="file-input"
                />
                {files.map((item, index) => (
                  <div key={index} className="track-row">
                    <div className="track-item">
                      <span className="file-name">Track {index + 1}: {item.file.name}</span>
                      <input
                        type="text"
                        value={item.title}
                        onChange={(e) => {
                          const updatedFiles = [...files];
                          updatedFiles[index].title = e.target.value;
                          setFiles(updatedFiles);
                        }}
                        className="form-input"
                        placeholder={`Enter title for Track ${index + 1}`}
                      />
                    </div>
                    <button
                      type="button"
                      className="remove-button"
                      onClick={() => handleRemoveFile(index)}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Cover Photo</label>
              <input
                type="file"
                accept=".jpg,.jpeg,.png,.gif,.webp,.bmp,.tiff,.heic,.ico,.raw,.svg"
                onChange={handleCoverPhotoChange}
                className="file-input"
              />
              {coverPhoto && <span className="file-name">Cover Photo Selected</span>}
            </div>

            <button type="submit" className="upload-button" disabled={loading}>
              {loading ? 'Uploading...' : 'Upload'}
            </button>
          </form>

          {loading && <div className="loader"></div>}
        </div>
      </div>
    </div>
  );
};

export default Upload;